import { ChangeEventsWritableStream } from './ChangeEventsWritableStream.mjs';
import { Startable } from '@hds/common-utils';
import { TypeUtilities } from '@hds/common-utils';

class ChangeEventsPipelineManager extends Startable {
    #_changeEventsProcessorFn;
    #_abortController;
    #_failureListeners = new Map();
    //Once started these will be populated
    #_changeEventsPipelinePromise;
    #_readableStream;
    #_writableStream;
    _projection;
    constructor(projection = undefined) {
        super();
        this._projection = projection;
    }
    /*****************************************************/
    /* START ABSTRACT METHODS */
    /*****************************************************/
    async _createChangeEventsReadableStream() {
        throw new Error('Subclass must implement _createChangeEventsReadableStream()!');
    }
    /*****************************************************/
    /* END ABSTRACT METHODS */
    /* START OVERRIDABLE METHODS */
    /*****************************************************/
    async _createChangeEventsWritableStream(changeEventsProcessorFn) {
        const writable = new ChangeEventsWritableStream(changeEventsProcessorFn);
        await writable.whenInitialized();
        return writable;
    }
    async #_establishPipeline() {
        this.#_abortController = new AbortController;
        this.#_readableStream = await this._createChangeEventsReadableStream();
        this.#_writableStream = await this._createChangeEventsWritableStream(this.#_changeEventsProcessorFn);

        this.#_changeEventsPipelinePromise = this.#_readableStream.pipeTo(this.#_writableStream.writableStream, { signal: this.#_abortController.signal })

        this.#_changeEventsPipelinePromise.catch((pipelineError) => {
            //We are using the abort mechanism to shutdown
            if (pipelineError?.message !== 'SHUTTING_DOWN' && pipelineError?.message !== 'RESTARTING') {
                this.#_triggerFailure(pipelineError);
            }
        });
    }
    async _start(changeEventsProcessorFn) {
        this.#_changeEventsProcessorFn = changeEventsProcessorFn;
        await this.#_establishPipeline();
        return super._start();
    }
    async _restart() {
        //NOTE: this one is confusing but the Stream API doesn't give us a great mechanism to restart streams so to restart we are recreating the pipeline.
        //This means any .then or .catch handlers registered with this.#_changeEventsPipelinePromise may become stale and we don't want to react to any failures
        this.removeAllFailureListeners();
        this.abort(new Error('RESTARTING'));
        await this.#_establishPipeline();
        return super._restart();
    }
    async _shutdown() {
        this.abort(new Error('SHUTTING_DOWN'));
        return super._shutdown();
    }
    /*****************************************************/
    /* END OVERRIDABLE METHODS */
    /*****************************************************/
    addFailureListener(callback, thisContext) {
        const changeListenerId = crypto.randomUUID();
        this.#_failureListeners.set(changeListenerId, { callback, thisContext });
        return changeListenerId;
    }
    removeFailureListener(changeListenerOrListenerId) {
        let foundChangeListenerId = changeListenerOrListenerId;
        if (TypeUtilities.isOfTypeFunction(changeListenerOrListenerId)) {
            for (const [changeListenerId, callbackDetails] of this.#_failureListeners) {
                if (callbackDetails.callback === changeListenerOrListenerId) {
                    foundChangeListenerId = changeListenerId;
                }
            }
        }
        if (foundChangeListenerId) {
            return this.#_failureListeners.delete(foundChangeListenerId);
        }
    }
    removeAllFailureListeners() {
        this.#_failureListeners.clear();
    }
    #_triggerFailure(failureError) {
        for (const [changeListenerId, callbackDetails] of this.#_failureListeners) {
            callbackDetails.callback.call(callbackDetails.thisContext, failureError);
        }
    }
    abort(abortError) {
        this.#_abortController.abort(abortError);
    }
    get promise() {
        return this.#_changeEventsPipelinePromise;
    }
    get readableStream() {
        return this.#_readableStream;
    }
    get writableStream() {
        return this.#_writableStream;
    }
}
export { ChangeEventsPipelineManager };
export default ChangeEventsPipelineManager;